/**
 * Club page — viewer state (handoff README §4 "Club variants (7)" + the `CLUBS` map in
 * handoff/Club Page.dc.html). Pure: decides which variant the viewer is in and what the
 * header pill / trust line / CTAs, the rail panel, the lock card, the status band and the
 * mobile sticky bar say. Every number comes from the DTOs handed in; nothing is invented.
 */
import { money, monthYearLabel, ago, dateLabel } from '../../utils/surfaceFormat';
import { GREEN, MUTED, AMBER_TEXT } from '../surface/tokens';

export const VARIANTS = ['public', 'private', 'paid', 'member', 'admin', 'pending', 'empty', 'blocked', 'invite'];

/** `/ MONTH`, `/ YEAR`, `ONCE` — the unit after a membership price. */
export function priceUnit(club) {
  if (!club || club.membershipMode === 'free' || !club.membershipMode) return '';
  if (club.membershipMode === 'subscription') return club.membershipBillingPeriod === 'annual' ? '/ YEAR' : '/ MONTH';
  return 'ONCE';
}

/** `$12.00 / MONTH` — or `FREE`. */
export function priceLabel(club) {
  if (!club || club.membershipMode === 'free' || !club.membershipMode || club.membershipPriceCents == null) return 'FREE';
  return `${money(club.membershipPriceCents)} ${priceUnit(club)}`.trim();
}

/** The paid-membership perk list is built from the club's real settings, never a stock list. */
export function membershipPerks(club) {
  if (!club) return [];
  const feed = ['The club feed'];
  if (club.forumEnabled) feed.push('the forum');
  if (club.memberSignalsEnabled !== false) feed.push('member Portals');
  const perks = [feed.length === 1 ? 'The club feed' : `${feed.slice(0, -1).join(', ')} and ${feed[feed.length - 1]}`];
  perks.push('Member-only events');
  if (club.gatheringsEnabled !== false) perks.push('Gatherings and gathering pricing');
  if (club.showMembersList !== false) perks.push('The members list');
  return perks;
}

/** Which variant the viewer is in. Precedence: member/admin → pending → blocked → invite → paid → private → public. */
export function clubVariant(club, { previewAsMember = false, postCount = null } = {}) {
  if (!club) return 'public';
  const role = club.userRole;
  const isActualAdmin = role === 'admin' || role === 'owner';
  if (isActualAdmin && !previewAsMember) return postCount === 0 ? 'empty' : 'admin';
  if (role) return 'member';
  if (club.hasPendingRequest) return 'pending';
  if (club.isBlocked) return 'blocked';
  if (club.hasPendingInvite) return 'invite';
  if (club.membershipMode && club.membershipMode !== 'free') return 'paid';
  if (club.isPublic === false) return 'private';
  return 'public';
}

function pillFor(club, variant, role) {
  if (variant === 'admin' || variant === 'empty') return role === 'owner' ? { label: 'OWNER', tone: 'cyan' } : { label: 'ADMIN', tone: 'black' };
  if (variant === 'member') return { label: 'MEMBER', tone: 'black' };
  if (club.membershipMode && club.membershipMode !== 'free') return { label: 'MEMBERS ONLY', tone: 'blush' };
  if (club.isPublic === false) return { label: 'PRIVATE', tone: 'hairline' };
  return { label: 'PUBLIC', tone: 'lime' };
}

function trustFor(club, variant) {
  switch (variant) {
    case 'public': return { label: 'OPEN TO ANYONE', color: '#000' };
    case 'private': return { label: 'BY REQUEST', color: MUTED };
    case 'paid': return { label: priceLabel(club), color: '#000' };
    case 'member': return { label: "YOU'RE IN", color: GREEN };
    case 'admin': return { label: 'YOU RUN THIS CLUB', color: '#000' };
    case 'empty': return { label: 'BRAND NEW', color: MUTED };
    case 'pending': return { label: 'REQUEST SENT', color: AMBER_TEXT };
    case 'invite': return { label: 'INVITED', color: GREEN };
    case 'blocked': return { label: 'NOT OPEN TO YOU', color: MUTED };
    default: return { label: '', color: MUTED };
  }
}

/** Whether every tab but ABOUT (and EVENTS on a paid club) is behind the lock. */
export function isLocked(club, variant) {
  if (variant === 'private' || variant === 'paid' || variant === 'blocked') return true;
  if (variant === 'pending' || variant === 'invite') return club.isPublic === false || (club.membershipMode && club.membershipMode !== 'free');
  return false;
}

/** A locked club still shows public events when the lock is the membership fee, not privacy. */
export function eventsVisibleWhenLocked(club) {
  return club.isPublic !== false;
}

function lockFor(club, variant) {
  const paid = club.membershipMode && club.membershipMode !== 'free';
  if (variant === 'pending') {
    return { title: 'WAITING ON AN ADMIN.', body: 'Your request is in. Nothing more to do — you get a notification the moment someone answers.', cta: 'WITHDRAW MY REQUEST', fill: false, action: 'withdraw' };
  }
  if (variant === 'blocked') {
    return { title: "YOU CAN'T JOIN THIS CLUB.", body: 'An admin has blocked you from joining. There is nothing to request.', cta: null, action: null };
  }
  if (variant === 'invite') {
    return { title: "YOU'RE INVITED.", body: 'A member invited you. Accept and you are in — no review, nothing to request.', cta: 'ACCEPT INVITE', fill: true, action: 'join' };
  }
  if (paid) {
    return { title: 'MEMBERS ONLY.', body: 'The feed, the forum and the member events are behind the membership. The club and its public events stay visible.', cta: `JOIN — ${priceLabel(club)}`, fill: true, action: 'join' };
  }
  return { title: 'THIS CLUB IS PRIVATE.', body: 'You can see the name, the tagline and how many people are in. Everything else — posts, the forum, who the members are — opens when an admin lets you in.', cta: 'REQUEST TO JOIN', fill: true, action: 'join' };
}

function membershipBody(club, membership) {
  if (!membership) return null;
  const status = (membership.paidStatus || 'free').toLowerCase();
  if (status === 'free' || club.membershipMode === 'free' || !club.membershipMode) {
    return { right: 'FREE', body: `In since ${monthYearLabel(membership.joinedAt || club.createdAt)}. Nothing to renew.`, fine: null };
  }
  const through = membership.paidThroughAt ? dateLabel(membership.paidThroughAt) : null;
  const price = membership.paidPriceCents != null ? money(membership.paidPriceCents) : (club.membershipPriceCents != null ? money(club.membershipPriceCents) : null);
  if (status === 'comp') return { right: 'COMP', body: through ? `On the house through ${through}.` : 'On the house. Nothing to renew.', fine: null };
  if (status === 'past_due') return { right: 'PAST DUE', body: 'The last renewal did not go through. Update your card to stay in.', fine: through ? `You keep access until ${through}.` : null };
  if (status === 'canceled' || membership.subscriptionCanceledAt) {
    return { right: 'CANCELLED', body: through ? `Cancelled — you are in until ${through}.` : 'Cancelled.', fine: null };
  }
  if (status === 'expired') return { right: 'EXPIRED', body: 'Your membership has ended. Join again to get back in.', fine: null };
  if (club.membershipMode === 'subscription') {
    return {
      right: 'ACTIVE',
      body: through && price ? `Paid up through ${through}. Renews at ${price}.` : through ? `Paid up through ${through}.` : 'Active.',
      fine: through ? `Cancelling keeps you in until ${through}.` : null,
    };
  }
  return { right: 'ACTIVE', body: through ? `Paid up through ${through}.` : 'Lifetime membership — nothing renews.', fine: null };
}

/**
 * Everything the page needs to say for this viewer.
 * @param club ClubDto · previewAsMember · postCount (announcements loaded; null while loading)
 * · membership (ClubMembershipDto | null) · ownerStats (ClubRevenueStatsDto | null) · now
 */
export function clubViewerState(club, {
  previewAsMember = false, postCount = null, membership = null, ownerStats = null, now = new Date(),
} = {}) {
  const variant = clubVariant(club, { previewAsMember, postCount });
  const role = club?.userRole || null;
  const isOwner = role === 'owner';
  const isActualAdmin = role === 'admin' || role === 'owner';
  const isAdmin = isActualAdmin && !previewAsMember;
  const isMember = !!role;
  const locked = club ? isLocked(club, variant) : false;
  const paid = !!(club?.membershipMode && club.membershipMode !== 'free');
  const price = club ? priceLabel(club) : 'FREE';
  const unit = club ? priceUnit(club) : '';
  const canInvite = isAdmin || (isMember && club?.anyoneCanInvite !== false);

  const pill = club ? pillFor(club, variant, role) : { label: 'PUBLIC', tone: 'lime' };
  const trust = club ? trustFor(club, variant) : { label: '', color: MUTED };

  let ctas = [];
  let rail = null;
  let mobBar = null;
  let band = null;
  const lock = club && locked ? lockFor(club, variant) : null;

  const sentAgo = club?.pendingRequestSentAt ? ago(club.pendingRequestSentAt, now) : null;
  const sentOn = club?.pendingRequestSentAt ? dateLabel(club.pendingRequestSentAt) : null;
  const memberCount = club?.memberCount ?? 0;

  switch (club ? variant : 'none') {
    case 'public': {
      const approval = club.requiresApproval === true;
      ctas = [{ label: approval ? 'REQUEST TO JOIN' : 'JOIN THE CLUB', fill: true, action: 'join' }, { label: 'SHARE', action: 'share' }];
      rail = {
        band: approval ? 'REQUEST TO JOIN' : 'JOIN', right: approval ? 'ADMIN APPROVES' : 'FREE',
        body: approval
          ? 'Public club — an admin approves each request. Posts, forum and the members list open up once you are in.'
          : 'Public club — you are in the moment you tap. Posts, forum and the members list open up right away.',
        cta: approval ? 'REQUEST TO JOIN' : 'JOIN THE CLUB', fill: true, action: 'join',
        cta2: 'JUST FOLLOW THE EVENTS', action2: 'events',
        fine: 'Leave whenever. Nothing is charged, ever.',
      };
      mobBar = { price: 'FREE', note: approval ? 'ADMIN APPROVES' : 'OPEN TO ANYONE', cta: approval ? 'REQUEST' : 'JOIN', action: 'join' };
      break;
    }
    case 'private':
      ctas = [{ label: 'REQUEST TO JOIN', fill: true, action: 'join' }, { label: 'SHARE', action: 'share' }];
      rail = {
        band: 'REQUEST TO JOIN', right: 'ADMIN REVIEWS',
        body: 'An admin reads your Portal Page and your Vibe Check, then decides. Most answer inside a day.',
        cta: 'REQUEST TO JOIN', fill: true, action: 'join',
        fine: 'You will get a notification either way. No means no hard feelings and no record.',
      };
      mobBar = { price: 'PRIVATE', note: 'ADMIN REVIEWS', cta: 'REQUEST', action: 'join' };
      break;
    case 'paid': {
      const sub = club.membershipMode === 'subscription';
      const period = club.membershipBillingPeriod === 'annual' ? 'year' : 'month';
      const term = club.membershipTermDays;
      ctas = [{ label: `JOIN — ${price}`, fill: true, action: 'join' }, { label: 'SHARE', action: 'share' }];
      rail = {
        band: 'MEMBERS ONLY', right: sub ? 'AUTO-RENEWS' : 'ONE-TIME',
        price: money(club.membershipPriceCents), priceNote: unit, perks: membershipPerks(club),
        body: '', cta: `JOIN — ${price}`, fill: true, action: 'join',
        cta2: 'SEE PUBLIC EVENTS ONLY', action2: 'events',
        fine: sub
          ? `Charged today, then the same day each ${period}. Cancel any time — you keep the ${period} you paid for.`
          : term ? `Charged once. Good for ${term} days, nothing renews.` : 'Charged once. Nothing renews.',
      };
      mobBar = { price: money(club.membershipPriceCents), note: sub ? `PER ${period.toUpperCase()}` : 'ONE-TIME', cta: 'JOIN', action: 'join' };
      break;
    }
    case 'member': {
      const m = membershipBody(club, membership);
      // Announcements are admin-only server-side (EnsureAdminOrOwner), so a member's primary
      // action is the thing they CAN post: a forum thread, else a signal, else an invite.
      const primary = club.forumEnabled
        ? { label: 'START A THREAD', fill: true, action: 'thread' }
        : club.memberSignalsEnabled !== false
          ? { label: 'OPEN A PORTAL', fill: true, action: 'signal' }
          : { label: canInvite ? 'INVITE A FRIEND' : 'SEE THE EVENTS', fill: true, action: canInvite ? 'invite' : 'events' };
      ctas = [primary];
      if (canInvite && primary.action !== 'invite') ctas.push({ label: 'INVITE A FRIEND', action: 'invite' });
      ctas.push({ label: 'MUTE', action: 'mute' });
      rail = {
        band: 'YOUR MEMBERSHIP', right: m?.right || (paid ? 'ACTIVE' : 'FREE'),
        body: m?.body || (paid ? 'Active.' : `In since ${monthYearLabel(club.createdAt)}. Nothing to renew.`),
        cta: primary.label, fill: true, action: primary.action,
        cta2: isOwner ? null : paid ? 'MANAGE MEMBERSHIP' : 'LEAVE THE CLUB', action2: paid ? 'manage-membership' : 'leave',
        fine: m?.fine || (isOwner ? 'Owners cannot leave their club.' : null),
      };
      break;
    }
    case 'admin':
    case 'empty': {
      const s = ownerStats;
      const lapsed = s ? (s.lapsedCount ?? ((s.pastDueCount || 0) + (s.expiredCount || 0))) : null;
      let body;
      if (s && paid) {
        body = isOwner && s.revenueThisMonthCents != null
          ? `${s.activeMembersCount ?? 0} paying, ${lapsed} lapsed, ${money(s.revenueThisMonthCents)} yours this month.`
          : `${s.activeMembersCount ?? 0} paying, ${lapsed} lapsed.`;
      } else if (paid) {
        body = `${memberCount} members. Membership is ${price}.`;
      } else {
        body = `${memberCount} ${memberCount === 1 ? 'member' : 'members'}. Free to join.`;
      }
      if (variant === 'empty') {
        ctas = [{ label: 'WRITE THE FIRST POST', fill: true, action: 'post' }, { label: 'INVITE FRIENDS', action: 'invite' }];
        rail = {
          band: 'GET IT STARTED', right: 'DAY ONE',
          body: `${memberCount} ${memberCount === 1 ? 'person' : 'people'}, nothing posted. Put one thing up and invite three friends — that is usually all it takes.`,
          cta: 'WRITE THE FIRST POST', fill: true, action: 'post',
          cta2: 'INVITE FRIENDS', action2: 'invite',
          fine: 'Members never see this panel or the admin controls in the feed.',
        };
      } else {
        ctas = [{ label: 'POST TO THE CLUB', fill: true, action: 'post' }, { label: 'CREATE AN EVENT', action: 'create-event' }];
        rail = {
          band: 'THIS CLUB', right: isOwner ? 'OWNER VIEW' : 'ADMIN VIEW', body,
          cta: isOwner ? 'MEMBERSHIPS & REVENUE →' : 'CLUB SETTINGS', fill: true, action: isOwner ? 'revenue' : 'manage',
          cta2: isOwner ? 'CLUB SETTINGS' : null, action2: 'manage',
          fine: 'Members never see this panel or the admin controls in the feed.',
        };
      }
      break;
    }
    case 'pending':
      ctas = [{ label: 'REQUESTED', disabled: true }, { label: 'WITHDRAW', action: 'withdraw' }];
      rail = {
        band: 'YOUR REQUEST', right: 'PENDING',
        body: `${sentOn ? `Sent ${sentOn}. ` : ''}An admin has it. Most clubs answer inside a day.`,
        cta: 'WITHDRAW MY REQUEST', fill: false, action: 'withdraw',
        fine: 'You can ask again later — withdrawing leaves no mark.',
      };
      band = { label: 'REQUESTED', value: 'An admin has your request', note: sentAgo ? `SENT ${sentAgo}` : null };
      mobBar = { price: 'PENDING', note: sentAgo ? `SENT ${sentAgo}` : 'ADMIN REVIEWS', cta: 'WITHDRAW', action: 'withdraw' };
      break;
    case 'blocked':
      ctas = [];
      rail = { band: 'THIS CLUB', right: 'UNAVAILABLE', body: "YOU CAN'T JOIN THIS CLUB.", cta: null, fine: 'An admin has blocked you from joining. There is nothing to request.' };
      break;
    case 'invite':
      ctas = [{ label: 'ACCEPT INVITE', fill: true, action: 'join' }];
      rail = {
        band: "YOU'RE INVITED", right: 'INVITE PENDING',
        body: 'A member invited you. Accept and you are in — no review, nothing to request.',
        cta: 'ACCEPT INVITE', fill: true, action: 'join',
        fine: paid ? `Membership is ${price}.` : null,
      };
      mobBar = { price: 'INVITED', note: 'NO REVIEW', cta: 'ACCEPT', action: 'join' };
      break;
    default:
      break;
  }

  return {
    variant, role, isOwner, isActualAdmin, isAdmin, isMember, locked, paid, canInvite,
    eventsOpen: !locked || (club ? eventsVisibleWhenLocked(club) : true),
    pill, trust, ctas, adminBar: isActualAdmin, rail, lock, band, mobBar, price, unit,
  };
}

export default clubViewerState;
